import React, { useState } from 'react'
import styled from 'styled-components'
import { Grid, Button, Slider, Input } from '@material-ui/core'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import Crop54OutlinedIcon from '@material-ui/icons/Crop54Outlined';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import PanToolOutlinedIcon from '@material-ui/icons/PanToolOutlined';
import UndoIcon from '@material-ui/icons/Undo';
import RedoIcon from '@material-ui/icons/Redo';

import { DrawSettingType } from './DrawToolContent';
import ColorPalette from './ColorPalette'

type MenuButtonProps = {
  selected: boolean;
}
const MenuButton = styled(Button)<MenuButtonProps>`
  display: grid;
  size: 'large';
  width: min(100%, 64px);
  margin-bottom: 20px;
  background-color: ${props => props.selected ? '#90caf9' : undefined };
`
const SliderMenuItem = styled(MenuItem)`
  width: 200px;
`
const SmallFiberManualRecordIcon = styled(FiberManualRecordIcon)`
  font-size: 8px;
`

type WidthSliderProps = {
  width: number;
  changeWidth: (value: number) => void;
}
const WidthSlider: React.FC<WidthSliderProps> = ({width, changeWidth}) => {
  return (
    <Grid container spacing={1}>
      <Grid item><SmallFiberManualRecordIcon/></Grid>
      <Grid item xs>
        <Slider
            value={width}
            onChange={(ev, newValue) => changeWidth(typeof newValue === 'number' ? newValue : newValue[0])}
            min={1}
            max={64}
            aria-labelledby="input-slider"
        />
      </Grid>
      <Grid item><FiberManualRecordIcon/></Grid>
      <Grid item>
        <Input
          value={width}
          margin="dense"
          onChange={(ev) => changeWidth(Number(ev.target.value))}
          inputProps={{
            step: 1,
            min: 1,
            max: 64,
            type: 'number',
            'aria-labelledby': 'input-slider'
          }}
        />
      </Grid>
    </Grid>
  );
}

type DrawToolsMenuProps = {
  setting: DrawSettingType;
  setSetting: (setting: DrawSettingType) => void;
  actionIdsLength: number;
  undoneActionsLength: number;
  undoAction: () => void;
  redoAction: () => void;
  addText: () => void;
}
const DrawToolsMenu: React.FC<DrawToolsMenuProps> = ({setting, setSetting, actionIdsLength, undoneActionsLength, undoAction, redoAction, addText}) => {
  const [penAnchorEl, setPenAnchorEl] = useState<null | HTMLElement>(null);
  const [eraserAnchorEl, setEraserAnchorEl] = useState<null | HTMLElement>(null);

  const penHandle = () => {
    setSetting({...setting, mode: 'pen'});
    setPenAnchorEl(null);
  }
  const penWidthHandle = (value: number) => {
    let fixedValue = value;
    if (value < 1) fixedValue = 1;
    if (value > 64) fixedValue = 64;
    setSetting({...setting, penWidth: fixedValue});
  }
  const penColorHandle = (value: string) => {
    setSetting({...setting, penColor: value});
  }
  const eraserHandle = () => {
    setSetting({...setting, mode: 'eraser'});
    setEraserAnchorEl(null);
  }
  const eraserWidthHandle = (value: number) => {
    let fixedValue = value;
    if (value < 1) fixedValue = 1;
    if (value > 64) fixedValue = 64;
    setSetting({...setting, eraserWidth: fixedValue});
  }
  const penMenuHandleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    setPenAnchorEl(ev.currentTarget);
  }
  const penMenuHandleClose = () => {
    setPenAnchorEl(null);
  }
  const eraserMenuHandleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    setEraserAnchorEl(ev.currentTarget);
  }
  const eraserMenuHandleClose = () => {
    setEraserAnchorEl(null);
  }
  const textHandle = () => {
    addText();
    moveHandle();
  }
  const moveHandle = () => {
    setSetting({...setting, mode: 'move'});
  }

  return (
    <>
      <MenuButton aria-controls="pen-menu" aria-haspopup="true" onClick={setting.mode === 'pen' ? penMenuHandleClick : penHandle} selected={setting.mode === 'pen'}>
        <EditOutlinedIcon />
      </MenuButton>
      <Menu id="pen-menu" anchorEl={penAnchorEl} keepMounted
        open={Boolean(penAnchorEl)}
        onClose={penMenuHandleClose}
      >
        <SliderMenuItem>
          <WidthSlider width={setting.penWidth} changeWidth={penWidthHandle} />
        </SliderMenuItem>
        <MenuItem>
          <ColorPalette
            currentValue={setting.penColor}
            onChange={penColorHandle}
            labelDisplay={false}
          />
        </MenuItem>
      </Menu>
      <MenuButton aria-controls="eraser-menu" aria-haspopup="true" onClick={setting.mode === 'eraser' ? eraserMenuHandleClick : eraserHandle} selected={setting.mode === 'eraser'}>
        <Crop54OutlinedIcon />
      </MenuButton>
      <Menu id="eraser-menu" anchorEl={eraserAnchorEl} keepMounted
        open={Boolean(eraserAnchorEl)}
        onClose={eraserMenuHandleClose}
      >
        <SliderMenuItem>
          <WidthSlider width={setting.eraserWidth} changeWidth={eraserWidthHandle} />
        </SliderMenuItem>
      </Menu>
      <MenuButton onClick={textHandle} selected={false}>
        <TextFieldsIcon />
      </MenuButton>
      <MenuButton onClick={moveHandle} selected={setting.mode === 'move'}>
        <PanToolOutlinedIcon />
      </MenuButton>
      <MenuButton onClick={undoAction} selected={false} disabled={actionIdsLength === 0}>
        <UndoIcon />
      </MenuButton>
      <MenuButton onClick={redoAction} selected={false} disabled={undoneActionsLength === 0}>
        <RedoIcon />
      </MenuButton>
    </>
  )
}

export default DrawToolsMenu;
